export const REASONING_EFFORTS = ["none", "low", "medium", "high", "xhigh", "max"] as const;

export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];

export const REASONING_EFFORT_LABELS: Record<ReasoningEffort, string> = {
  none: "Aucun",
  low: "Faible",
  medium: "Moyen",
  high: "Élevé",
  xhigh: "Très élevé",
  max: "Maximum",
};

export interface AiModelDefinition {
  id: string;
  label: string;
  group: "GPT-5.6" | "GPT-5.5" | "GPT-5.4" | "Codex";
  description: string;
  efforts: readonly ReasoningEffort[];
  fallbackEffort: ReasoningEffort;
}

const GPT_56_EFFORTS = ["none", "low", "medium", "high", "xhigh", "max"] as const;
const GENERAL_EFFORTS = ["none", "low", "medium", "high", "xhigh"] as const;
const PRO_EFFORTS = ["medium", "high", "xhigh"] as const;
const CODEX_EFFORTS = ["low", "medium", "high", "xhigh"] as const;

/**
 * Curated models that support this app's Responses API flow: streaming plus
 * local function tools. Keep this catalog shared with the Cloudflare proxy so
 * a model shown in the UI can never be rejected by a stale server allowlist.
 */
export const AI_MODELS = [
  {
    id: "gpt-5.6-sol",
    label: "GPT-5.6 Sol",
    group: "GPT-5.6",
    description: "Le modèle GPT-5.6 le plus capable.",
    efforts: GPT_56_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.6-terra",
    label: "GPT-5.6 Terra",
    group: "GPT-5.6",
    description: "Équilibre entre capacité et coût.",
    efforts: GPT_56_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.6-luna",
    label: "GPT-5.6 Luna",
    group: "GPT-5.6",
    description: "Optimisé pour les volumes élevés et l'efficacité.",
    efforts: GPT_56_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.6",
    label: "GPT-5.6 (alias Sol)",
    group: "GPT-5.6",
    description: "Alias officiel qui pointe actuellement vers GPT-5.6 Sol.",
    efforts: GPT_56_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.5",
    label: "GPT-5.5",
    group: "GPT-5.5",
    description: "Génération précédente polyvalente.",
    efforts: GENERAL_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.4",
    label: "GPT-5.4",
    group: "GPT-5.4",
    description: "Modèle généraliste GPT-5.4.",
    efforts: GENERAL_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.4-pro",
    label: "GPT-5.4 Pro",
    group: "GPT-5.4",
    description: "Plus de calcul pour les tâches difficiles; plus lent et coûteux.",
    efforts: PRO_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.4-mini",
    label: "GPT-5.4 mini",
    group: "GPT-5.4",
    description: "Option rapide et économique.",
    efforts: GENERAL_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.4-nano",
    label: "GPT-5.4 nano",
    group: "GPT-5.4",
    description: "Option la plus légère pour les gros volumes.",
    efforts: GENERAL_EFFORTS,
    fallbackEffort: "medium",
  },
  {
    id: "gpt-5.3-codex",
    label: "GPT-5.3 Codex",
    group: "Codex",
    description: "Ancien modèle spécialisé pour le développement agentique.",
    efforts: CODEX_EFFORTS,
    fallbackEffort: "medium",
  },
] as const satisfies readonly AiModelDefinition[];

export type AiModelId = (typeof AI_MODELS)[number]["id"];

export const DEFAULT_MODEL: AiModelId = "gpt-5.6-sol";
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = "high";

const MODELS_BY_ID = new Map<string, AiModelDefinition>(
  AI_MODELS.map((model) => [model.id, model]),
);

export function getModelDefinition(model: string): AiModelDefinition | undefined {
  return MODELS_BY_ID.get(model);
}

export function isAllowedModel(value: unknown): value is AiModelId {
  return typeof value === "string" && MODELS_BY_ID.has(value);
}

export function isReasoningEffort(value: unknown): value is ReasoningEffort {
  return typeof value === "string" && (REASONING_EFFORTS as readonly string[]).includes(value);
}

export function modelSupportsEffort(model: string, effort: unknown): effort is ReasoningEffort {
  const definition = getModelDefinition(model);
  return Boolean(
    definition &&
    isReasoningEffort(effort) &&
    (definition.efforts as readonly string[]).includes(effort),
  );
}
